// ActionUpdatesTab — append-only update/comment feed for an action.
// Post new updates at the top, list existing updates newest-first.
// New pattern — not present in events or controls.

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { actionsApi, type ActionUpdate } from '@/lib/api/actions'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Button } from '@/components/ui/button'
import { MessageSquare, Send, User } from 'lucide-react'

function relativeTime(isoString: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export function ActionUpdatesTab() {
  const { slug } = useTenant()
  const { actionId } = useParams({ strict: false }) as { actionId: string }
  const api = useApi()
  const actApi = actionsApi(api)
  const queryClient = useQueryClient()

  const [newUpdate, setNewUpdate] = useState('')

  const {
    data: updates,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.actions.updates(slug, actionId),
    queryFn: () => actApi.listUpdates(actionId),
  })

  const postMutation = useMutation({
    mutationFn: () => actApi.postUpdate(actionId, { update_text: newUpdate.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.updates(slug, actionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
      setNewUpdate('')
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load updates"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Post new update */}
      <div className="mb-6 rounded-lg border p-4">
        <textarea
          className="mb-3 min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="Add an update or comment..."
          value={newUpdate}
          onChange={(e) => setNewUpdate(e.target.value)}
        />
        <div className="flex justify-end">
          {postMutation.isError && (
            <span className="mr-auto text-sm text-destructive">
              {(postMutation.error as Error).message || 'Failed to post update'}
            </span>
          )}
          <Button
            size="sm"
            onClick={() => postMutation.mutate()}
            disabled={postMutation.isPending || !newUpdate.trim()}
          >
            <Send className="mr-1 h-3.5 w-3.5" />
            {postMutation.isPending ? 'Posting...' : 'Post Update'}
          </Button>
        </div>
      </div>

      {/* Updates list */}
      {!updates || updates.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No updates yet.</p>
            <p className="text-xs text-muted-foreground/60">
              Post an update above to track progress on this action.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-0 divide-y rounded-lg border">
          {updates.map((update) => (
            <UpdateRow key={update.id} update={update} />
          ))}
        </div>
      )}
    </div>
  )
}

function UpdateRow({ update }: { update: ActionUpdate }) {
  return (
    <div className="px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        <User className="h-3 w-3" />
        <span className="font-medium">{update.created_by_name}</span>
        <span>·</span>
        <span title={new Date(update.created_at).toLocaleString()}>
          {relativeTime(update.created_at)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm">{update.update_text}</p>
    </div>
  )
}
